/**
 * Audit Working Paper Service
 * 工作底稿管理服務 - 提供工作底稿的獨立CRUD操作
 * 對應後端 AuditWorkingPaperController
 */

import { BASE_AUDIT_API, authorizedFetch, fetchAPI, getHttpErrorMessage } from '@/services/api';
import { mergeWorkingPaperQueryResponse } from '@/services/auditService/workingPaperQueryMerge';
import { useWpSignoffStore } from '@/store/audit/wpSignoffStore';
import { requiresWpGlobalTx } from '@/services/auditService/wpCrossServiceCodes';
import { runWpSaveWithGlobalTx } from '@/services/auditService/wpSaveTxService';
import {
  getActiveWpDataMode,
  getActiveWpCode,
  getActiveWpSpanType,
  getActiveWpYearOfAssessment,
  isActiveWpPlTotalView,
} from '@/services/auditService/wpPeriodYoaBridge';
import {
  buildYoaMapBatchPayload,
  getWpYoaMapCache,
  upsertWpYoaBucketDetails,
} from '@/services/auditService/wpYoaMapCache';
import {
  resolveWpDataMode,
  wpDataModeNeedsYoa,
  wpDataModeUsesBatchSave,
  type WpDataMode,
} from '@/utils/auditPeriodWpMulti';
import type {
  WorkingPaperSaveRequest,
  WorkingPaperSaveResponse,
  WorkingPaperBatchSaveRequest,
  WorkingPaperBatchSaveResponse,
  WorkingPaperQueryRequest,
  WorkingPaperQueryResponse,
  WorkingPaperSummary,
  WorkingPaperData,
} from '@/services/auditService/interface/working-paper';
import { notifyFsAdjustmentSilentRefresh } from '@/utils/fsAdjustmentRefresh';

const base = `${BASE_AUDIT_API}/working-paper`;

function tryParseFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
  const raw = match?.[1] || match?.[2];
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw.replace(/\+/g, '%20'));
  } catch {
    return raw.replace(/\+/g, ' ');
  }
}

function resolveDataModeForCode(code: string, spanHint?: string | null): WpDataMode {
  const active = getActiveWpDataMode();
  if (active !== 'single') return active;
  // DialogLauncher already stamped this WP session as SINGLE — do not promote via
  // request YOA (components often pass meta.yoa for labels even under SINGLE).
  // Code alias mismatch (e.g. open BS-NCL-BLS / save BS-CL-BLS) must still honor SINGLE.
  if (getActiveWpCode() && getActiveWpSpanType() === 'SINGLE') {
    return 'single';
  }
  // Fallback when bridge unset: MULTI only if caller passed YOA / WP session span MULTI
  if (spanHint === 'MULTI' || getActiveWpYearOfAssessment()) {
    return resolveWpDataMode(code, 'MULTI');
  }
  return resolveWpDataMode(code, 'SINGLE');
}

/**
 * MULTI 会话判定（集中化：paper-registry 全部组件统一走 batchSaveOrUpdate）：
 * 1. DialogLauncher bridge spanType === MULTI（优先于请求 YOA）；
 * 2. bridge 未设 / 非 SINGLE 时，请求自带 yearOfAssessment → MULTI；
 * 3. bridge code 匹配且 spanType === MULTI → MULTI。
 * resolveWpDataMode 再按 code 细分：PL-* / BS 白名单 → bs-multi（batch）；
 * 非白名单 BS 保持 single（engagement 级数据，#20 单年契约）。
 * SINGLE 会话不得因请求带 YOA 被抬升为 MULTI（否则无 YOA Map 缓存会误抛 P8）。
 */
function resolveSaveSpanHint(code: string, yoa?: string | null): string | null {
  if (getActiveWpCode() && getActiveWpSpanType() === 'SINGLE') {
    return null;
  }
  if (yoa && String(yoa).trim()) return 'MULTI';
  const bridgeCode = getActiveWpCode();
  const codeMatches = !!bridgeCode && bridgeCode === String(code || '').trim().toUpperCase();
  if (codeMatches && getActiveWpSpanType() === 'MULTI') return 'MULTI';
  return null;
}

function resolveSaveYoa(params: WorkingPaperSaveRequest, mode: WpDataMode): string | null {
  if (!wpDataModeNeedsYoa(mode)) return null;
  const fromParams = typeof params.yearOfAssessment === 'string'
    ? params.yearOfAssessment.trim()
    : '';
  if (fromParams) return fromParams;
  const fromDetails = params.details?.yearOfAssessment;
  if (typeof fromDetails === 'string' && fromDetails.trim()) {
    return fromDetails.trim();
  }
  return getActiveWpYearOfAssessment();
}

function withSignoffDetails(params: WorkingPaperSaveRequest): WorkingPaperSaveRequest {
  const yoa = typeof params.yearOfAssessment === 'string' ? params.yearOfAssessment.trim() : '';
  const patch = useWpSignoffStore.getState().toSavePatch({
    companyId: params.companyId,
    annualId: params.annualId,
    code: params.code,
    yearOfAssessment: yoa,
  });
  return {
    ...params,
    details: { ...(params.details || {}), ...patch },
  };
}

async function postSaveOrUpdate(params: WorkingPaperSaveRequest) {
  const body = withSignoffDetails(params);
  return fetchAPI<WorkingPaperSaveResponse>(`${base}/saveOrUpdate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

/**
 * 保存或更新工作底稿（獨立操作）
 * P7/P8 + MULTI 集中分流（paper-registry 全部组件统一入口）：
 * - MULTI（请求带 yearOfAssessment，或 DialogLauncher WP 会话 spanType=MULTI）
 *   且 code 为 PL-* / BS 白名单 → bs-multi：全量 YOA Map + batchSaveOrUpdate
 *   （POST /working-paper/batchSaveOrUpdate；各组件点击 Save 均经此端点落库）
 * - MULTI 下非白名单 BS / SINGLE → 现网 saveOrUpdate 单年契约（#20）
 * - bs-multi 但无法解析出活动 YOA（且无 byYearOfAssessmentPayload）→ 回退单年保存，
 *   避免把缓存里的陈旧 bucket 整包提交造成编辑丢失
 *
 * 成功后通知已挂载的 FsAdjustment 做无感刷新（WP 保存后不再关闭弹窗）。
 */
export const saveOrUpdate = async (params: WorkingPaperSaveRequest) => {
  if (isActiveWpPlTotalView()) {
    throw new Error('Total view is read-only. Switch to period tabs to edit working papers.');
  }

  // Add Back / similar: must hit flat saveOrUpdate so details.addBackSourceCodes is not lost
  // in YOA-map batch assembly (missing in-memory cache → stale/empty selection).
  if (params.forceSingleSave) {
    const yoa = typeof params.yearOfAssessment === 'string' ? params.yearOfAssessment.trim() : '';
    const details = yoa
      ? {...(params.details || {}), yearOfAssessment: yoa}
      : stripYoaFromDetails(params.details || {});
    const singleParams: WorkingPaperSaveRequest = {
      ...params,
      details,
      yearOfAssessment: yoa || undefined,
      forceSingleSave: undefined,
    };
    const result = requiresWpGlobalTx(params.code)
      ? await runWpSaveWithGlobalTx(() => postSaveOrUpdate(singleParams))
      : await postSaveOrUpdate(singleParams);
    notifyFsAdjustmentSilentRefresh(params.companyId, params.annualId, params.code);
    return result;
  }

  const mode = resolveDataModeForCode(params.code, resolveSaveSpanHint(params.code, params.yearOfAssessment));
  const yoa = resolveSaveYoa(params, mode);

  // P8：白名单 MULTI — 全量 YOA Map via batchSaveOrUpdate
  if (wpDataModeUsesBatchSave(mode) && (yoa || params.byYearOfAssessmentPayload)) {
    if (yoa && params.details) {
      upsertWpYoaBucketDetails(
        params.companyId,
        params.annualId,
        params.code,
        yoa,
        params.details,
      );
    }

    let payload = params.byYearOfAssessmentPayload || null;
    if (!payload) {
      payload = buildYoaMapBatchPayload(
        params.companyId,
        params.annualId,
        params.code,
        params.issueMark ?? null,
      );
    } else if (params.issueMark && !payload.issueMark) {
      payload = { ...payload, issueMark: params.issueMark };
    }

    if (!payload) {
      throw new Error(
        'P8 bs-multi save requires a full byYearOfAssessment map (load WP before save)',
      );
    }

    const cache = getWpYoaMapCache(params.companyId, params.annualId, params.code);
    const detailsArr = Array.isArray(payload.details) ? payload.details : null;
    const icBag = detailsArr && detailsArr[0] && typeof detailsArr[0] === 'object'
      ? detailsArr[0]
      : payload;
    const isIcInverted = String(params.code || '').trim().toUpperCase() === 'BS-EQ-IC'
      && (cache?.yoaOrder?.length ?? 0) >= 2
      && icBag?.shareholders
      && typeof icBag.shareholders === 'object'
      && !Array.isArray(icBag.shareholders);
    if (cache && cache.yoaOrder.length > 0) {
      if (isIcInverted) {
        const shKeys = Object.keys(icBag.shareholders);
        const missing = cache.yoaOrder.filter((k) => !shKeys.includes(k));
        if (missing.length > 0) {
          throw new Error(
            `P8 BS-EQ-IC MULTI requires full-year shareholders map; missing YOA: ${missing.join(', ')}`,
          );
        }
      } else {
        const missing = cache.yoaOrder.filter((k) => !payload![k]);
        if (missing.length > 0) {
          throw new Error(
            `P8 whitelist MULTI requires full-year batch save; missing YOA: ${missing.join(', ')}`,
          );
        }
      }
    }

    const runBatch = () =>
      batchSaveOrUpdate({
        companyId: params.companyId,
        annualId: params.annualId,
        workingPapers: { [params.code]: payload! },
      }).then((batchResult) => ({
        companyId: params.companyId,
        annualId: params.annualId,
        code: params.code,
        details: params.details,
        batchResult,
      } as WorkingPaperSaveResponse));

    const batchResult = requiresWpGlobalTx(params.code)
      ? await runWpSaveWithGlobalTx(runBatch)
      : await runBatch();
    notifyFsAdjustmentSilentRefresh(params.companyId, params.annualId, params.code);
    return batchResult;
  }

  const details = yoa
    ? { ...(params.details || {}), yearOfAssessment: yoa }
    : stripYoaFromDetails(params.details || {});

  // 1.1 PL·MULTI（+ YOA）与 1.3 / SINGLE：Facade 单条保存
  const singleParams: WorkingPaperSaveRequest = {
    ...params,
    details,
    ...(yoa ? { yearOfAssessment: yoa } : { yearOfAssessment: undefined }),
  };
  const result = requiresWpGlobalTx(params.code)
    ? await runWpSaveWithGlobalTx(() => postSaveOrUpdate(singleParams))
    : await postSaveOrUpdate(singleParams);
  notifyFsAdjustmentSilentRefresh(params.companyId, params.annualId, params.code);
  return result;
};

function stripYoaFromDetails(details: Record<string, any>): Record<string, any> {
  if (!details || typeof details !== 'object') return details || {};
  if (!('yearOfAssessment' in details)) return details;
  const { yearOfAssessment: _removed, ...rest } = details;
  return rest;
}

/**
 * 批量保存工作底稿
 * POST /working-paper/batchSaveOrUpdate
 * P8：白名单 payload 已是 YOA Map 时不再注入单片 YOA。
 */
export const batchSaveOrUpdate = (params: WorkingPaperBatchSaveRequest) => {
  if (isActiveWpPlTotalView()) {
    return Promise.reject(
      new Error('Total view is read-only. Switch to period tabs to edit working papers.'),
    );
  }
  const { companyId, annualId, workingPapers } = params;
  const mode = getActiveWpDataMode();
  const yoa = wpDataModeNeedsYoa(mode) ? getActiveWpYearOfAssessment() : null;
  const papers = { ...workingPapers };

  // Only inject scalar YOA into flat (non-Map) payloads — legacy P7 path
  if (yoa && mode === 'bs-multi') {
    for (const code of Object.keys(papers)) {
      const detail = papers[code];
      if (!detail || typeof detail !== 'object') continue;
      const hasYoaKey = Object.keys(detail).some((k) => /^\d{4}\/\d{2}$/.test(k));
      if (hasYoaKey) continue; // P8 YOA Map — do not flatten
      // BS-EQ-IC MULTI inverted: { details: [{ shareholders: {yoa→[]} }] } — do not inject scalar YOA
      if (String(code || '').trim().toUpperCase() === 'BS-EQ-IC') {
        const detailsArr = Array.isArray((detail as any).details) ? (detail as any).details : null;
        const bag = detailsArr?.[0] && typeof detailsArr[0] === 'object' ? detailsArr[0] : detail;
        const sh = bag?.shareholders;
        if (sh && typeof sh === 'object' && !Array.isArray(sh)
          && Object.keys(sh).some((k: string) => /^\d{4}\/\d{2}$/.test(k))) {
          continue;
        }
      }
      if (!detail.yearOfAssessment) {
        papers[code] = { ...detail, yearOfAssessment: yoa };
      }
    }
  }

  return fetchAPI<WorkingPaperBatchSaveResponse>(
    `${base}/batchSaveOrUpdate?companyId=${companyId}&annualId=${annualId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(papers),
    }
  );
};

/**
 * 查詢工作底稿
 * GET /working-paper/get
 * P8：bs-multi 走 yoaMap=true（权威 details=YOA Map）；pl-slice 仍单片 YOA。
 */
export const getWorkingPaper = async (params: WorkingPaperQueryRequest): Promise<WorkingPaperData[]> => {
  const { companyId, annualId, code, workflow } = params;
  const mode = resolveDataModeForCode(
    code,
    params.yearOfAssessment || params.yoaMap || params.multiPeriods ? 'MULTI' : undefined,
  );
  const needsYoa = wpDataModeNeedsYoa(mode);
  const explicitYoa = (typeof params.yearOfAssessment === 'string' && params.yearOfAssessment.trim())
    ? params.yearOfAssessment.trim()
    : null;

  const useYoaMap = mode === 'bs-multi' || Boolean(params.yoaMap);

  let url = `${base}/get?companyId=${companyId}&annualId=${annualId}&code=${encodeURIComponent(code)}`;
  if (workflow) {
    url += `&workflow=${workflow}`;
  }

  if (useYoaMap) {
    url += `&yoaMap=true`;
  } else {
    const yoa = needsYoa
      ? (explicitYoa || getActiveWpYearOfAssessment())
      : null;
    if (yoa) {
      url += `&yearOfAssessment=${encodeURIComponent(yoa)}`;
    }
  }

  const result = await fetchAPI<WorkingPaperQueryResponse | WorkingPaperData[]>(url, {
    method: 'GET',
  });

  if (Array.isArray(result)) {
    return result as WorkingPaperData[];
  }

  // Prefer caller YOA for Map projection — never fall back to a stale bridge YOA
  // when the request already carries yearOfAssessment (PL Multiple year-tab opens).
  return mergeWorkingPaperQueryResponse(result, {
    companyId,
    annualId,
    code,
    activeYoa: explicitYoa ?? getActiveWpYearOfAssessment(),
  });
};

/**
 * 獲取工作底稿匯總數據
 * GET /working-paper/summary
 */
export const getSummaryData = (params: WorkingPaperQueryRequest) => {
  const { companyId, annualId, code } = params;

  return fetchAPI<WorkingPaperSummary>(
    `${base}/summary?companyId=${companyId}&annualId=${annualId}&code=${code}`,
    {
      method: 'GET',
    }
  );
};

/**
 * 列出某個年度的所有工作底稿編碼
 * GET /working-paper/listCodes
 */
export const listCodesByAnnual = (annualId: number) => {
  return fetchAPI<string[]>(`${base}/listCodes?annualId=${annualId}`, {
    method: 'GET',
  });
};

/**
 * 逻辑删除工作底稿中的单行数据
 * DELETE /working-paper/deleteRow
 */
export const deleteWpRow = (params: {
  companyId: number;
  annualId: number;
  code: string;
  rowType: string;
  rowId: number;
}) => {
  const { companyId, annualId, code, rowType, rowId } = params;
  return fetchAPI<boolean>(
    `${base}/deleteRow?companyId=${companyId}&annualId=${annualId}&code=${code}&rowType=${rowType}&rowId=${rowId}`,
    { method: 'DELETE' }
  );
};

/**
 * Query linked WP adjustments before removing a director from O1.
 * GET /working-paper/directorRemovalImpact
 */
export const getDirectorRemovalImpact = (params: {
  companyId: number;
  annualId: number;
  directorId: number;
}) => {
  const { companyId, annualId, directorId } = params;
  return fetchAPI<import('./interface/director-removal').DirectorRemovalImpact>(
    `${base}/directorRemovalImpact?companyId=${companyId}&annualId=${annualId}&directorId=${directorId}`,
    { method: 'GET' }
  );
};

/**
 * Query linked LX2 vehicle loans before deleting a hire-purchase motor row from F1.
 * GET /working-paper/ppeDeleteImpact
 */
export const getPpeDeleteImpact = (params: {
  companyId: number;
  annualId: number;
  ppeId: number;
}) => {
  const { companyId, annualId, ppeId } = params;
  return fetchAPI<import('./interface/ppe-delete').PpeDeleteImpact>(
    `${base}/ppeDeleteImpact?companyId=${companyId}&annualId=${annualId}&ppeId=${ppeId}`,
    { method: 'GET' }
  );
};

/**
 * RX10：同侧 taxable ↔ capital 调整挂接迁移（互换 attr.rowId）。
 * POST /working-paper/exchange/reclassify-nature
 */
export const reclassifyExchangeNature = (params: {
  companyId: number;
  annualId: number;
  side: 'gain' | 'loss';
  direction: 'toTaxable' | 'toCapital';
}) => {
  return fetchAPI<boolean>(`${base}/exchange/reclassify-nature`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
};

/**
 * Download blank Excel template for F1 / RX18.
 * GET /working-paper/excel-template?code=
 */
export async function downloadWpExcelTemplate(code: string): Promise<{ blob: Blob; filename?: string }> {
  // Must use authorizedFetch so path is ciphered when payload cipher is on.
  const res = await authorizedFetch(
    `${base}/excel-template?code=${encodeURIComponent(code)}`,
    { method: 'GET' },
  );
  if (!res.ok) {
    throw new Error(await getHttpErrorMessage(res, 'Template download failed'));
  }
  // Gateway crypto errors often return HTTP 200 + JSON Result (e.g. code 4605).
  const ct = (res.headers.get('Content-Type') || '').toLowerCase();
  if (ct.includes('json')) {
    throw new Error(await getHttpErrorMessage(res, 'Template download failed'));
  }
  const blob = await res.blob();
  const filename = tryParseFilenameFromContentDisposition(res.headers.get('Content-Disposition'));
  return { blob, filename };
}

/**
 * Download filled 租約委任書 (zh_Hant / en).
 * POST /working-paper/rental/lease-appointment-letter
 * Accepts draft snapshot so download works before the rental row is saved.
 */
export async function downloadLeaseAppointmentLetter(params: {
  companyId: number;
  annualId: number;
  rentalRowId?: number | null;
  lang: 'zh_Hant' | 'en';
  tenant?: string;
  address?: string;
  landlord?: string;
  fromDate?: string;
  toDate?: string;
  monthlyRent?: string;
}): Promise<{ blob: Blob; filename?: string }> {
  // Must use authorizedFetch so path/body are ciphered when payload cipher is on.
  // Binary docx responses stay unencrypted (CryptoPayloadFilter only wraps JSON).
  const res = await authorizedFetch(`${base}/rental/lease-appointment-letter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: params.companyId,
      annualId: params.annualId,
      rentalRowId: params.rentalRowId ?? undefined,
      lang: params.lang,
      tenant: params.tenant ?? '',
      address: params.address ?? '',
      landlord: params.landlord ?? '',
      fromDate: params.fromDate ?? '',
      toDate: params.toDate ?? '',
      monthlyRent: params.monthlyRent ?? '',
    }),
  });

  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);

  // Backend / gateway may return JSON error with non-2xx or even misleading headers
  const looksJson =
    contentType.includes('application/json') ||
    (bytes.length > 0 && (bytes[0] === 0x7b /* { */ || bytes[0] === 0x5b /* [ */));
  const looksDocx = bytes.length > 3 && bytes[0] === 0x50 && bytes[1] === 0x4b; // PK

  if (!res.ok || looksJson || !looksDocx) {
    let message = 'Lease appointment letter download failed';
    if (looksJson) {
      try {
        const json = JSON.parse(new TextDecoder().decode(bytes));
        message = json?.msg || json?.message || message;
      } catch {
        // keep default
      }
    } else if (!res.ok) {
      try {
        const text = new TextDecoder().decode(bytes).trim();
        if (text) message = text;
      } catch {
        // keep default
      }
    } else if (!looksDocx) {
      message = 'Downloaded content is not a valid Word document';
    }
    throw new Error(message);
  }

  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const filename = tryParseFilenameFromContentDisposition(res.headers.get('Content-Disposition'));
  return { blob, filename };
}

/**
 * Trigger browser download for a blob.
 */
export function triggerBlobDownload(blob: Blob, filename: string) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Delay revoke — immediate revoke can cancel download in some browsers
  window.setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(blobUrl);
  }, 1500);
}

/**
 * Import Excel for F1 / RX18 — returns details JSON (does not persist).
 * POST /working-paper/excel-import
 */
export async function importWpExcel(params: {
  code: string;
  file: File;
  companyId?: number;
  annualId?: number;
}): Promise<Record<string, any>> {
  const { code, file, companyId, annualId } = params;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('code', code);
  if (companyId != null) formData.append('companyId', String(companyId));
  if (annualId != null) formData.append('annualId', String(annualId));

  // Must use authorizedFetch so path is ciphered when payload cipher is on.
  // FormData body stays multipart (backend CryptoPayloadFilter skips multipart).
  const res = await authorizedFetch(`${base}/excel-import`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    throw new Error(await getHttpErrorMessage(res, 'Excel import failed'));
  }
  const result = await res.json();
  if (result.code !== 0) {
    throw new Error(result.msg || 'Excel import failed');
  }
  return result.data as Record<string, any>;
}

/** IR56B OCR meta for employer / period mismatch checks (not persisted). */
export interface Ir56bOcrMeta {
  employerFileNo?: string | null;
  employerName?: string | null;
  periods?: string[];
  pageCount?: number;
  employeeCount?: number;
  warnings?: string[];
}

/** POST /working-paper/ocrIr56b — parse-only; salary[] matches excel-import shape. */
export interface Ir56bOcrResult {
  salary: Array<{
    id?: number;
    rowKey?: string;
    name?: string;
    salary?: number;
    lastYearFinal?: number;
  }>;
  meta?: Ir56bOcrMeta;
}

export interface Ir56bOcrJob {
  jobId: string;
  status: string;
  currentPage: number;
  pageCount: number;
  namedCount: number;
  errorMessage?: string | null;
  result?: Ir56bOcrResult | null;
}

export interface Ir56bOcrProgress {
  currentPage: number;
  pageCount: number;
  namedCount: number;
  status: string;
}

async function unwrapIr56bJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    throw new Error(await getHttpErrorMessage(res, fallback));
  }
  const body = await res.json();
  if (body.code !== 0) {
    throw new Error(body.msg || fallback);
  }
  return body.data as T;
}

/**
 * Start page-by-page IR56B AI/OCR. Poll {@link getIr56bOcrJob} until DONE.
 */
export async function startIr56bOcr(params: {
  file: File;
  companyId?: number;
  annualId?: number;
}): Promise<Ir56bOcrJob> {
  const { file, companyId, annualId } = params;
  const formData = new FormData();
  formData.append('file', file);
  if (companyId != null) formData.append('companyId', String(companyId));
  if (annualId != null) formData.append('annualId', String(annualId));
  // Must use authorizedFetch so path is ciphered when payload cipher is on.
  // FormData body stays multipart (backend CryptoPayloadFilter skips multipart).
  const res = await authorizedFetch(`${base}/ocrIr56b?async=1`, {
    method: 'POST',
    body: formData,
  });
  return unwrapIr56bJson<Ir56bOcrJob>(res, 'IR56B OCR failed to start');
}

export async function getIr56bOcrJob(jobId: string): Promise<Ir56bOcrJob> {
  // Must use authorizedFetch so path is ciphered when payload cipher is on.
  const res = await authorizedFetch(
    `${base}/ocrIr56b?jobId=${encodeURIComponent(jobId)}`,
    { method: 'GET' },
  );
  return unwrapIr56bJson<Ir56bOcrJob>(res, 'IR56B OCR job not found');
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * OCR IR56B PDF → RX18 salary rows (does not persist).
 * Starts a job and polls page progress so the UI can show “page x of y”.
 */
export async function ocrIr56b(
  params: {
    file: File;
    companyId?: number;
    annualId?: number;
  },
  onProgress?: (progress: Ir56bOcrProgress) => void,
): Promise<Ir56bOcrResult> {
  const t0 = performance.now();
  console.warn('[IR56B-OCR][fe] START-JOB', {
    fileName: params.file?.name,
    fileSize: params.file?.size,
    companyId: params.companyId,
    annualId: params.annualId,
  });
  const started = await startIr56bOcr(params);
  onProgress?.({
    currentPage: started.currentPage || 0,
    pageCount: started.pageCount || 0,
    namedCount: started.namedCount || 0,
    status: started.status,
  });
  const deadline = Date.now() + 20 * 60 * 1000;
  while (Date.now() < deadline) {
    const job = await getIr56bOcrJob(started.jobId);
    onProgress?.({
      currentPage: job.currentPage || 0,
      pageCount: job.pageCount || 0,
      namedCount: job.namedCount || 0,
      status: job.status,
    });
    const status = String(job.status || '').toUpperCase();
    if (status === 'DONE') {
      if (!job.result) {
        throw new Error('IR56B OCR finished with empty result');
      }
      console.warn('[IR56B-OCR][fe] DONE', {
        jobId: job.jobId,
        pages: job.pageCount,
        named: job.namedCount,
        elapsedMs: Math.round(performance.now() - t0),
      });
      return job.result;
    }
    if (status === 'ERROR') {
      throw new Error(job.errorMessage || 'IR56B OCR failed');
    }
    await sleep(800);
  }
  throw new Error('IR56B OCR timed out while reading pages');
}

/**
 * 工作底稿服務對象
 */
export const auditWorkingPaperService = {
  saveOrUpdate,
  batchSaveOrUpdate,
  getWorkingPaper,
  getSummaryData,
  listCodesByAnnual,
  deleteWpRow,
  getDirectorRemovalImpact,
  getPpeDeleteImpact,
  reclassifyExchangeNature,
  downloadWpExcelTemplate,
  downloadLeaseAppointmentLetter,
  importWpExcel,
  ocrIr56b,
  triggerBlobDownload,
};

// 導出所有類型定義
export type {
  WorkingPaperSaveRequest,
  WorkingPaperSaveResponse,
  WpIssueMarkPayload,
  WorkingPaperBatchSaveRequest,
  WorkingPaperBatchSaveResponse,
  WorkingPaperQueryRequest,
  WorkingPaperQueryResponse,
  WorkingPaperSummary,
  WorkingPaperData,
} from '@/services/auditService/interface/working-paper';
